import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NotifikationerProvider } from "@/contexts/NotifikationerContext";
import Dashboard from "@/pages/Dashboard";
import LoginPage from "@/pages/LoginPage";
import UsersPage from "@/pages/UsersPage";
import ElevereSamletPage from "@/pages/ElevereSamletPage";
import AfsluttendeEksamenPage from "@/pages/AfsluttendeEksamenPage";
import MedarbejderePage from "@/pages/MedarbejderePage";
import MedarbejdereProfilPage from "@/pages/MedarbejdereProfilPage";
import LokationerPage from "@/pages/LokationerPage";
import TimegodkendelsePage from "@/pages/TimegodkendelsePage";
import FlexPage from "@/pages/FlexPage";
import HubPage from "@/pages/HubPage";
import VagtplanPage from "@/pages/VagtplanPage";
import MaalOgReglerPage from "@/pages/MaalOgReglerPage";
import ListerPage from "@/pages/ListerPage";
import UndervisningPage from "@/pages/UndervisningPage";
import NotifikationerPage from "@/pages/NotifikationerPage";
import InstallPrompt from "@/components/InstallPrompt";
import PullToRefresh from "@/components/PullToRefresh";
import { useEffect, Component } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(err) {
    return { error: err };
  }
  componentDidCatch(err, info) {
    console.error("React render crash:", err, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, padding: 24, background: "#0f172a", color: "#f1f5f9" }}>
          <p style={{ fontWeight: 700, fontSize: 16 }}>Der opstod en fejl — prøv at genindlæse siden.</p>
          <p style={{ fontSize: 13, color: "#94a3b8" }}>{this.state.error?.message || "Ukendt fejl"}</p>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", borderRadius: 12, background: "#FF2A6D", color: "#fff", fontWeight: 600, fontSize: 14, border: "none", cursor: "pointer" }}>
            Genindlæs
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading) {
      // Minimum 800ms splash så spinneren altid ses
      const elapsed = Date.now() - (window.__splashStart || Date.now());
      const delay = Math.max(0, 800 - elapsed);
      setTimeout(function() {
        if (window.__hideSplash) window.__hideSplash();
      }, delay);
    }
  }, [loading]);

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/elever"
        element={
          <ProtectedRoute>
            <ElevereSamletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/elev-status"
        element={<Navigate to="/admin/elever" replace />}
      />
      <Route
        path="/admin/elev/:appItemId/eksamen"
        element={
          <ProtectedRoute>
            <AfsluttendeEksamenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/medarbejdere"
        element={
          <ProtectedRoute>
            <MedarbejderePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/medarbejdere/:item_id"
        element={
          <ProtectedRoute>
            <MedarbejdereProfilPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lokationer"
        element={
          <ProtectedRoute>
            <LokationerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/timegodkendelse"
        element={
          <ProtectedRoute>
            <TimegodkendelsePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/flex"
        element={
          <ProtectedRoute>
            <FlexPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hub"
        element={
          <ProtectedRoute>
            <HubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vagtplan"
        element={
          <ProtectedRoute>
            <VagtplanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/maal-og-regler"
        element={
          <ProtectedRoute>
            <MaalOgReglerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lister"
        element={
          <ProtectedRoute>
            <ListerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/undervisning"
        element={
          <ProtectedRoute>
            <UndervisningPage />
          </ProtectedRoute>
        }
      />
      <Route path="/chat" element={<Navigate to="/" replace />} />
      <Route
        path="/notifikationer"
        element={
          <ProtectedRoute>
            <NotifikationerPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <NotifikationerProvider>
              <AppRoutes />
              <InstallPrompt />
              <PullToRefresh />
            </NotifikationerProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
